'use client'

import React, { useCallback, useEffect, useState } from 'react'
import dynamic from 'next/dynamic'
import { readAuthorizedAccountId } from '../lib/authActionGateClient'

const InviteFriendRuntime = dynamic(() => import('./InviteFriendProvider'), {
  ssr: false,
})

const isBrowser = () => typeof window !== 'undefined'

function readUnifiedAccountId() {
  if (!isBrowser()) return null
  return String(readAuthorizedAccountId() || '').trim() || null
}
function readAccountFromAuthEvent(event) {
  return String(
    event?.detail?.accountId ||
    event?.detail?.asherId ||
    event?.detail?.walletAddress ||
    readAuthorizedAccountId() ||
    '',
  ).trim() || null
 }

export default function InviteFriendHost() {
  const [runtime, setRuntime] = useState(null) // { key, accountId, reason }

  const openRuntime = useCallback((accountOverride) => {
    const currentUid = readUnifiedAccountId()
    const effectiveUid = currentUid || accountOverride
    if (!effectiveUid) return

    setRuntime({
      key: `forum-header:${effectiveUid}:${Date.now()}`,
      accountId: effectiveUid,
      reason: 'manual',
    })
  }, [])

  const disposeRuntime = useCallback(() => {
    setRuntime(null)
  }, [])

  // Keep an already open runtime tied to the current authenticated account.
  useEffect(() => {
    if (!isBrowser()) return

    const handleAuth = (event) => {
      const acc = readAccountFromAuthEvent(event)
      if (!acc) return
      setRuntime((current) => (current && current.accountId !== acc ? null : current))
    }

    const handleLogout = () => {
      setRuntime(null)
    }

    window.addEventListener('auth:ok', handleAuth)
    window.addEventListener('auth:success', handleAuth)
    window.addEventListener('auth:logout', handleLogout)

    return () => {
      window.removeEventListener('auth:ok', handleAuth)
      window.removeEventListener('auth:success', handleAuth)
      window.removeEventListener('auth:logout', handleLogout)
    }
  }, [])

  // The invite runtime has exactly one opener: the authenticated button in the
  // forum header controls. There is deliberately no activity listener, timer,
  // interval or persisted auto-open clock in this global host.
  useEffect(() => {
    if (!isBrowser()) return

    const handleManualOpen = (e) => {
      if (String(e?.detail?.source || '') !== 'forum-invite-button') return
      const override = e?.detail?.accountId || null
      openRuntime(override)
    }

    window.addEventListener('invite:open', handleManualOpen)
    return () => {
      window.removeEventListener('invite:open', handleManualOpen)
    }
  }, [openRuntime])

  if (!runtime) return null

  return (
    <InviteFriendRuntime
      key={runtime.key}
      accountId={runtime.accountId}
      mode={runtime.reason}
      onDispose={disposeRuntime}
    />
  )
}
